import { h, mount } from '../lib/dom';
import { api, ApiClientError, type Item, type ListItemsParams } from '../lib/api';

/**
 * Renders an items table for the given filter params.
 *
 * Pure-function pattern: each call replaces the contents of `target`.
 * Re-rendering with new params is just calling `renderItemsTable` again.
 */
export async function renderItemsTable(target: HTMLElement, params: ListItemsParams) {
  // Loading state
  mount(target, h('div', { class: 'state' }, 'Loading items…'));

  let items: Item[];
  try {
    const result = await api.items.list(params);
    items = result.data;
  } catch (err) {
    const message = err instanceof ApiClientError ? err.message : 'Unknown error';
    mount(target, h('div', { class: 'state error' }, `Could not load items: ${message}`));
    return;
  }

  if (items.length === 0) {
    mount(target, h('div', { class: 'state' }, 'No items match these filters.'));
    return;
  }

  const table = h(
    'table',
    { class: 'data-table' },
    h(
      'thead',
      {},
      h(
        'tr',
        {},
        h('th', {}, 'Name'),
        h('th', {}, 'Category'),
        h('th', {}, 'Status'),
        h('th', {}, 'Created'),
      ),
    ),
    h('tbody', {}, ...items.map(rowFor)),
  );

  mount(target, table);
}

function rowFor(item: Item): HTMLTableRowElement {
  return h(
    'tr',
    {},
    h('td', {}, h('strong', {}, item.name), item.description ? h('div', { class: 'muted' }, item.description) : null),
    h('td', {}, item.category),
    h('td', {}, h('span', { class: 'badge', 'data-status': item.status }, item.status)),
    h('td', { class: 'muted' }, formatDate(item.created_at)),
  );
}

function formatDate(iso: string): string {
  // Lightweight; swap for Intl.DateTimeFormat if you need locale-aware formatting.
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
